import React, { useState } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Card,
  CardContent,
  Button,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  Chat as ChatIcon,
  Psychology as PsychologyIcon,
  AutoAwesome as AutoAwesomeIcon
} from '@mui/icons-material';
import ConversationList from '../components/ConversationList';
import ConversationInterface from '../components/ConversationInterface';
import { Conversation } from '../types';
import { useAuth } from '../contexts/AuthContext';

const ConversationsPage: React.FC = () => {
  const [selectedConversationId, setSelectedConversationId] = useState<string | null>(null);
  const [showMobileChat, setShowMobileChat] = useState(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { user } = useAuth();
  const currentUserId = user?.id || '';

  const handleConversationSelect = (conversationId: string) => {
    setSelectedConversationId(conversationId);
    if (isMobile) {
      setShowMobileChat(true);
    }
  };

  const handleConversationCreate = (conversation: Conversation) => {
    setSelectedConversationId(conversation.id);
    if (isMobile) {
      setShowMobileChat(true);
    }
  };

  const handleBackToList = () => {
    setShowMobileChat(false);
    setSelectedConversationId(null);
  };

  const renderWelcomeScreen = () => (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 4
      }}
    >
      <Box textAlign="center" maxWidth={600}>
        <ChatIcon sx={{ fontSize: 80, color: 'primary.main', mb: 3 }} />
        
        <Typography variant="h4" gutterBottom>
          Your Personal Wardrobe Assistant
        </Typography>
        
        <Typography variant="body1" color="textSecondary" paragraph>
          Get personalized styling advice, outfit recommendations, and wardrobe organization help. 
          I remember your preferences and learn from our conversations to provide better suggestions over time.
        </Typography>

        <Grid container spacing={2} sx={{ mt: 4 }}>
          <Grid size={4}>
            <Card sx={{ height: '100%' }}>
              <CardContent sx={{ textAlign: 'center' }}>
                <PsychologyIcon sx={{ fontSize: 40, color: 'primary.main', mb: 2 }} />
                <Typography variant="h6" gutterBottom>
                  Smart Memory
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  I remember your style preferences, sizes, and favorite brands across all conversations.
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid size={4}>
            <Card sx={{ height: '100%' }}>
              <CardContent sx={{ textAlign: 'center' }}>
                <AutoAwesomeIcon sx={{ fontSize: 40, color: 'secondary.main', mb: 2 }} />
                <Typography variant="h6" gutterBottom>
                  Personalized Advice
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Get outfit suggestions based on your actual wardrobe items and personal style.
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid size={4}>
            <Card sx={{ height: '100%' }}>
              <CardContent sx={{ textAlign: 'center' }}>
                <ChatIcon sx={{ fontSize: 40, color: 'success.main', mb: 2 }} />
                <Typography variant="h6" gutterBottom>
                  Natural Conversation
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Ask questions naturally - "What should I wear to dinner?" or "Show me my blue dresses"
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Typography variant="h6" sx={{ mt: 4, mb: 2 }}>
          Try asking me:
        </Typography>
        
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, alignItems: 'center' }}>
          {[
            "What should I wear to a job interview?",
            "Help me organize my summer wardrobe",
            "Show me outfits with my black blazer",
            "I have a date tonight, what should I wear?",
            "What items am I not wearing enough?"
          ].map((suggestion, index) => (
            <Typography
              key={index}
              variant="body2"
              sx={{
                bgcolor: 'grey.100',
                px: 2,
                py: 1,
                borderRadius: 2,
                fontStyle: 'italic',
                maxWidth: '80%'
              }}
            >
              "{suggestion}"
            </Typography>
          ))}
        </Box>
      </Box>
    </Box>
  );

  // Mobile layout
  if (isMobile) {
    if (showMobileChat && selectedConversationId) {
      return (
        <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
          <Paper sx={{ p: 2, borderRadius: 0 }}>
            <Button onClick={handleBackToList}>
              ← Back to Conversations
            </Button>
          </Paper>
          <Box sx={{ flexGrow: 1, overflow: 'hidden' }}>
            <ConversationInterface
              conversationId={selectedConversationId}
              currentUserId={currentUserId}
            />
          </Box>
        </Box>
      );
    } else {
      return (
        <Box sx={{ height: '100vh' }}>
          <ConversationList
            currentUserId={currentUserId}
            selectedConversationId={selectedConversationId || undefined}
            onConversationSelect={handleConversationSelect}
            onConversationCreate={handleConversationCreate}
          />
        </Box>
      );
    }
  }

  // Desktop layout
  return (
    <Box sx={{ height: '100vh', p: 2 }}>
      <Grid container spacing={2} sx={{ height: '100%' }}>
        <Grid size={4}>
          <Paper sx={{ height: '100%', overflow: 'hidden' }}>
            <ConversationList
              currentUserId={currentUserId}
              selectedConversationId={selectedConversationId || undefined}
              onConversationSelect={handleConversationSelect}
              onConversationCreate={handleConversationCreate}
            />
          </Paper>
        </Grid>
        
        <Grid size={8}>
          <Paper sx={{ height: '100%', overflow: 'hidden' }}>
            {selectedConversationId ? (
              <ConversationInterface
                conversationId={selectedConversationId}
                currentUserId={currentUserId}
              />
            ) : (
              renderWelcomeScreen()
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ConversationsPage;